/**
 * ScheduleSurgeon — Weekly Forward Planner (Schedule Surgeon rebuild, Stage 4)
 *
 * NBA (volume) and NFL (matchup) are a genuine fork off one endpoint
 * (Directional Change 8): NBA shows game count per week, NFL shows the next
 * five matchups rated for the player's own side of the ball. Every cell's
 * colour comes straight off the `tone` the API already computed — this page
 * never re-derives green/orange/red from a threshold of its own. Streamers
 * are grouped by the real light week(s) the backend identified, from the
 * same free-agent pool the Waiver Wire builds. No hardcoded preview/mock
 * data path — every other rebuilt tool in this series dropped its
 * IS_PREVIEW block once real data existed; this one does the same.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import ToolHeader from '../components/ToolHeader'
import JaiRichText from '../components/JaiRichText'
import PlayerPhoto from '../components/PlayerPhoto'
import TeamChip from '../components/TeamChip'
import StickyAskBar from '../components/StickyAskBar'
import UpgradePrompt from '../components/UpgradePrompt'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Card, StatStrip, V2SectionHead, PosChip, solidChip, pctTone, UpdatedRow, Notice } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'

// NFL's subtitle used to hardcode "five" regardless of how many columns the
// backend actually sent (buildNflColumns' own default changed independently
// — see scheduleSurgeonService.js) — derive it from the real column count instead.
const subtitleFor = (mode, columnCount) => {
  if (mode === 'volume') return 'Game volume per roster spot'
  if (!columnCount) return 'Your upcoming matchups, rated'
  return `Your next ${columnCount} matchup${columnCount === 1 ? '' : 's'}, rated`
}

// The week-by-week grid uses fixed 92px cells per column (a game-count or
// matchup rating needs to stay legible, not shrink), so on a phone-width
// screen a full NBA month or NFL rest-of-season view can need more room than
// the viewport has. Shared by the header row and every PlayerRow so both
// scroll together inside one horizontal-scroll box instead of the page
// itself gaining a scrollbar — see the "wide-table rule", Phase 3.
const NAME_COL_PX = 140
const CELL_COL_PX = 92
const TOTAL_COL_PX = 72
const GRID_GAP_PX = 10
const gridTemplateFor = (columnCount) => `minmax(${NAME_COL_PX}px, 1fr) repeat(${columnCount}, ${CELL_COL_PX}px) ${TOTAL_COL_PX}px`
const gridMinWidthFor = (columnCount) =>
  NAME_COL_PX + columnCount * CELL_COL_PX + TOTAL_COL_PX + (columnCount + 1) * GRID_GAP_PX

// Tone → colour is a straight LOOKUP off the `tone` the API already
// computed, never a game-count/rank threshold recomputed client-side — the
// whole point of Directional Change 9. Full-strength fills via solidChip
// (Aug 2026 solid-fill pass), same three named V2 tokens, no new hex values.
const TONE_STYLE = {
  green: solidChip(V2.success),
  orange: solidChip(V2.warning),
  red: solidChip(V2.error),
  neutral: solidChip(V2.faint),
}

// ── One grid cell ────────────────────────────────────────────────────────

function GridCell({ cell, mode }) {
  const style = TONE_STYLE[cell.tone] || TONE_STYLE.neutral
  return (
    <div style={{
      height: 34, borderRadius: 8, background: style.background, color: style.color,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 800, fontFamily: JAI_FONTS.mono, lineHeight: 1.15, padding: '2px 4px',
    }}>
      <span>{cell.label}</span>
      {mode === 'volume' && cell.backToBacks > 0 && (
        <span style={{ fontSize: 8.5, fontWeight: 700, opacity: 0.85 }}>{cell.backToBacks} B2B</span>
      )}
      {mode === 'matchup' && cell.defenceRank != null && (
        <span style={{ fontSize: 8.5, fontWeight: 700, opacity: 0.75 }}>#{cell.defenceRank}</span>
      )}
    </div>
  )
}

// ── One player row (photo/name/chips, then one GridCell per column) ──────

// Plain-text tone colour (not a chip fill) — separate from TONE_STYLE, which
// is sized for a background+contrast-ink pairing, not bare text on white.
const TONE_TEXT_COLOR = { green: V2.success, orange: V2.warning, red: V2.error, neutral: V2.faint }

function PlayerRow({ row, columns, mode, sport, isLast }) {
  const grid = gridTemplateFor(columns.length)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', borderBottom: isLast ? 'none' : '1px solid rgba(10,31,68,0.05)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: grid, gap: GRID_GAP_PX, alignItems: 'center', padding: '10px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <PlayerPhoto playerId={row.playerId} name={row.name} size={30} rounded="full" />
          <span style={{ fontSize: 13, fontWeight: 700, color: V2.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
          <PosChip>{row.position}</PosChip>
          <TeamChip abbr={row.team} sport={sport} />
        </div>
        {row.cells.map((cell) => <GridCell key={cell.key} cell={cell} mode={mode} />)}
        {mode === 'volume' ? (
          <span style={{ textAlign: 'right', fontSize: 14, fontWeight: 800, color: V2.navy, fontFamily: JAI_FONTS.mono }}>
            {row.total}
          </span>
        ) : (
          <span style={{
            textAlign: 'right', fontSize: 14, fontWeight: 800, fontFamily: JAI_FONTS.mono,
            color: row.total != null ? (TONE_TEXT_COLOR[row.totalTone] || V2.navy) : V2.faint,
          }}>
            {row.total != null ? row.total : '—'}
          </span>
        )}
      </div>
      {row.note && (
        <div style={{ padding: '0 20px 10px 56px' }}>
          <JaiRichText content={row.note} style={{ fontSize: 12, color: V2.mute }} />
        </div>
      )}
    </div>
  )
}

// ── Streamer card ───────────────────────────────────────────────────────

function StreamerCard({ player, leagueId, sport }) {
  return (
    <V2Card pad="14px 18px" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <PlayerPhoto playerId={player.playerId} name={player.name} size={36} rounded="full" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: V2.navy }}>{player.name}</span>
          {player.position && <PosChip>{player.position}</PosChip>}
          <TeamChip abbr={player.team} sport={sport} />
          <span style={{ fontSize: 11, fontWeight: 700, color: V2.blue, fontFamily: JAI_FONTS.mono }}>{player.games} GAMES</span>
        </div>
        <div style={{ fontSize: 12.5, color: V2.mute, marginTop: 3, lineHeight: 1.45 }}>
          {player.reason || 'A real streamer candidate on your wire this week.'}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {player.ownershipPercent != null && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: pctTone(player.ownershipPercent), fontFamily: JAI_FONTS.mono, lineHeight: 1 }}>{player.ownershipPercent}%</div>
            <div style={{ fontSize: 9.5, letterSpacing: '0.12em', color: V2.faint, fontFamily: JAI_FONTS.mono, marginTop: 4, textTransform: 'uppercase' }}>Owned</div>
          </div>
        )}
        <Link to={`/league/${leagueId}/waiver?target=${encodeURIComponent(player.name)}`} style={{ fontSize: 11.5, fontWeight: 700, color: V2.blue, textDecoration: 'none' }}>
          Add on Waiver Wire →
        </Link>
      </div>
    </V2Card>
  )
}

// ── Main ───────────────────────────────────────────────────────────────

export default function ScheduleSurgeon() {
  const { id } = useParams()

  const [league, setLeague] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [upgrade, setUpgrade] = useState(null)

  const sport = (league?.sport || 'nba').toLowerCase()

  const load = useCallback(async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true)
    setError('')
    setUpgrade(null)
    try {
      const res = await leaguesApi.getScheduleSurgeon(id, { refresh })
      setData(res.data)
    } catch (err) {
      if (err.response?.data?.code === 'PLAN_UPGRADE_REQUIRED') {
        setUpgrade({ tool: err.response.data.requiredTool, message: err.response.data.error })
      } else {
        setError(err.response?.data?.error || "JAI couldn't load Schedule Surgeon right now. Try again in a minute.")
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [id])

  useEffect(() => {
    let cancelled = false
    leaguesApi.get(id).then((r) => { if (!cancelled) setLeague(r.data.league) }).catch(() => {})
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const mode = data?.mode || (sport === 'nfl' ? 'matchup' : 'volume')
  const hasGrid = (data?.columns?.length || 0) > 0 && (data?.players?.length || 0) > 0

  // NFL equivalent of NBA's "light week" section — "light weeks"/streamer
  // candidates are a games-per-week concept that has no NFL meaning
  // (nothing to stream more of; every team plays once), so that section
  // stays empty for NFL by design (data.lightWeeks is always [] there —
  // see scheduleSurgeonService.js). This is the natural NFL substitute:
  // the real players (from row.total/totalTone, not re-derived) facing the
  // toughest average matchup over the window.
  const toughestMatchups = useMemo(() => {
    if (mode !== 'matchup') return []
    return (data?.players || [])
      .filter((p) => p.total != null)
      .sort((a, b) => a.total - b.total) // lower rank = tougher matchup
      .slice(0, 3)
  }, [data, mode])

  const columnLabelByKey = useMemo(
    () => new Map((data?.columns || []).map((c) => [c.key, c.label])),
    [data]
  )

  const statCells = useMemo(() => {
    if (!data?.totals?.length) return []
    return data.totals.map((t, i) => [String(t.value), (columnLabelByKey.get(t.key) || t.key).replace('Week ', 'W'), i === 0])
  }, [data, columnLabelByKey])

  // Prior-season honesty footnote (Directional Change 14 / scheduleService's
  // own honesty rule) — only when we actually know the league's real
  // current season to compare against, never a guess.
  const priorSeasonNote = (data?.basisSeason != null && league?.season != null && data.basisSeason < league.season)
    ? `Difficulty ratings are based on the ${data.basisSeason} season — this season's defences haven't played enough games yet.`
    : null

  // Defence ratings aren't warmed AT ALL (data.basisSeason is null, not just
  // a prior season) — every cell in a 3-week NFL grid reads neutral in that
  // case, which is far more visible with only 3 columns than it was across
  // the old 5-week grid. A quiet neutral grid with no explanation reads as
  // "JAI has nothing to say," not "the data isn't warmed yet" — say so.
  const ratingsUnavailableNote = (mode === 'matchup' && hasGrid && data?.basisSeason == null)
    ? "Defence difficulty ratings aren't warmed for this league yet, so every matchup below reads neutral — check back once JAI has run its daily update."
    : null

  const askBarContext = useMemo(() => {
    if (!hasGrid) return undefined
    const lightWeeksText = (data.lightWeeks || [])
      .map((lw) => `- ${columnLabelByKey.get(lw.key) || lw.key}: ${lw.reason}`)
      .join('\n')
    const flaggedText = (data.players || [])
      .filter((p) => p.note)
      .map((p) => `- ${p.name} (${p.position || '?'}, ${p.team || '?'}): ${p.note}`)
      .join('\n')
    return `THE USER IS LOOKING AT SCHEDULE SURGEON RIGHT NOW (${mode === 'volume' ? 'NBA game volume' : 'NFL matchup difficulty'}).\n` +
      `Light week(s):\n${lightWeeksText || 'None — no week is meaningfully lighter than the rest.'}\n` +
      `Players JAI flagged:\n${flaggedText || 'None.'}\n` +
      `JAI's insight: ${data.insight}`
  }, [data, hasGrid, mode, columnLabelByKey])

  return (
    <AppShell>
      <div style={{ padding: '30px 40px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 920, margin: '0 auto', paddingBottom: 100 }}>
          <ToolHeader
            leagueName={league?.name}
            title="Schedule Surgeon"
            subtitle={subtitleFor(mode, data?.columns?.length)}
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
          />

          {/* Totals strip, top-centre — NBA only; NFL has no equivalent, so
              it's omitted rather than filled with something else. */}
          {mode === 'volume' && statCells.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8 }}>
              <StatStrip cells={statCells} />
            </div>
          )}

          {/* "Updated N hours ago" + refresh */}
          {hasGrid && (
            <div style={{ marginBottom: 8 }}>
              <UpdatedRow asOf={data.asOf} onRefresh={() => load({ refresh: true })} refreshing={refreshing} credits="scheduleSurgeon" />
            </div>
          )}

          {priorSeasonNote && (
            <p style={{ textAlign: 'center', fontSize: 11, color: V2.faint, marginTop: 0, marginBottom: 16, fontStyle: 'italic' }}>
              {priorSeasonNote}
            </p>
          )}

          {ratingsUnavailableNote && (
            <p style={{ textAlign: 'center', fontSize: 11, color: V2.warning, marginTop: 0, marginBottom: 16, fontStyle: 'italic' }}>
              {ratingsUnavailableNote}
            </p>
          )}

          {upgrade && <UpgradePrompt tool={upgrade.tool} message={upgrade.message} />}

          {/* Error banner, with retry */}
          {error && !upgrade && (
            <Notice tone="error" action="Try again" onAction={() => load()}>{error}</Notice>
          )}

          {/* Inline spinner while the first load is in flight — never a
              full-page loader; header/skeleton above render immediately. */}
          {loading && !error && !upgrade && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
              <JAIMascot size={56} state="thinking" animate={true} />
              <p style={{ fontSize: 13.5, color: V2.faint, margin: 0 }}>Building your schedule read…</p>
            </div>
          )}

          {/* Honest empty state — pre-draft, empty roster, or no schedule
              available; data.insight carries the real reason. Never a blank grid. */}
          {!loading && !error && data && !hasGrid && (
            <V2Card pad="32px 24px" style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 13.5, color: 'rgba(10,31,68,0.72)', margin: 0 }}>
                {data.insight || "JAI can't build a schedule read for this roster right now."}
              </p>
            </V2Card>
          )}

          {/* Real content */}
          {!loading && !error && hasGrid && (
            <>
              {/* ── Grid ─────────────────────────────────────────────────── */}
              <V2Card pad="0" style={{ marginBottom: 14 }}>
                {/* Horizontal scroll lives on this wrapper only — never the
                    page — so a wide week-by-week grid stays usable one-handed
                    without forcing the whole screen to scroll sideways. */}
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <div style={{ minWidth: gridMinWidthFor(data.columns.length) }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: gridTemplateFor(data.columns.length),
                      gap: GRID_GAP_PX, padding: '11px 20px', borderBottom: '1px solid rgba(10,31,68,0.07)',
                      fontSize: 9.5, fontWeight: 700, letterSpacing: '0.12em', color: V2.faint,
                      fontFamily: JAI_FONTS.mono, textTransform: 'uppercase',
                    }}>
                      <span>Player</span>
                      {data.columns.map((c) => <span key={c.key} style={{ textAlign: 'center' }}>{c.label}</span>)}
                      {/* NBA: total games over the window. NFL: average opposing-
                          defence rank (upcomingFor's own aggregate) — same column
                          slot, different unit, so the header says which. */}
                      <span style={{ textAlign: 'right' }}>{mode === 'volume' ? 'Total' : 'Avg Rank'}</span>
                    </div>
                    {data.players.map((row, i) => (
                      <PlayerRow key={row.playerId ?? row.name} row={row} columns={data.columns} mode={mode} sport={sport} isLast={i === data.players.length - 1} />
                    ))}
                  </div>
                </div>
              </V2Card>

              {/* ── Insight — a plain one-liner, no mascot, no button ───────── */}
              {data.insight && (
                <p style={{ fontSize: 13, color: V2.mute, textAlign: 'center', margin: '0 0 26px', lineHeight: 1.5 }}>
                  {data.insight}
                </p>
              )}

              {/* ── Streamers, grouped by light week — NBA only. "Light week"
                  is a games-per-week concept with no NFL meaning (every team
                  plays once), so data.lightWeeks is always [] for NFL and
                  this section correctly renders nothing there. ─────────── */}
              {(data.lightWeeks || []).length > 0 && (
                <>
                  {data.lightWeeks.map((lw) => {
                    const group = (data.streamers || []).find((s) => s.weekKey === lw.key)
                    const label = columnLabelByKey.get(lw.key) || lw.key
                    return (
                      <div key={lw.key} style={{ marginBottom: 20 }}>
                        <V2SectionHead>{label} — your lightest</V2SectionHead>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {group?.players?.length ? (
                            group.players.map((p) => (
                              <StreamerCard key={p.playerId ?? p.name} player={p} leagueId={id} sport={sport} />
                            ))
                          ) : (
                            <p style={{ fontSize: 12.5, color: V2.faint, margin: 0 }}>No real streamer candidates on the wire right now.</p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </>
              )}

              {/* ── NFL's equivalent of the light-week section above —
                  the real players (from the grid's own total/totalTone,
                  never re-derived) facing the toughest average matchup over
                  the window. ─────────────────────────────────────────── */}
              {mode === 'matchup' && toughestMatchups.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <V2SectionHead>Toughest upcoming matchups</V2SectionHead>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {toughestMatchups.map((p) => (
                      <V2Card key={p.playerId ?? p.name} pad="14px 18px" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <PlayerPhoto playerId={p.playerId} name={p.name} size={32} rounded="full" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: V2.navy }}>{p.name}</span>
                            {p.position && <PosChip>{p.position}</PosChip>}
                            <TeamChip abbr={p.team} sport={sport} />
                          </div>
                          {p.note && <div style={{ fontSize: 12.5, color: V2.mute, marginTop: 3, lineHeight: 1.45 }}>{p.note}</div>}
                        </div>
                        <div style={{ textAlign: 'center', flexShrink: 0 }}>
                          <div style={{ fontSize: 19, fontWeight: 800, color: TONE_TEXT_COLOR[p.totalTone] || V2.navy, fontFamily: JAI_FONTS.mono, lineHeight: 1 }}>
                            {p.total}
                          </div>
                          <div style={{ fontSize: 9.5, letterSpacing: '0.12em', color: V2.faint, fontFamily: JAI_FONTS.mono, marginTop: 4, textTransform: 'uppercase' }}>
                            Avg Rank
                          </div>
                        </div>
                      </V2Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <StickyAskBar
        leagueId={id}
        scope="schedule"
        context={askBarContext}
        placeholder="Ask JAI about your schedule"
      />
    </AppShell>
  )
}
